import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import EdgeReport from './EdgeReport';
import './Dashboard.css';

interface Trade {
  id: string;
  market: string;
  side: 'BUY' | 'SELL';
  price: number;
  amount: number;
  pnl: number;
  timestamp: string;
  status: 'OPEN' | 'CLOSED';
  resolveTime?: string;
  marketId?: string;
  reasoning?: string;
  closeReason?: string;
  closedAt?: string;
}

interface Position {
  market: string;
  marketId?: string;
  side?: 'YES' | 'NO';
  shares: number;
  avgPrice: number;
  currentPrice: number;
  pnl: number;
  openedAt?: string;
  resolveTime?: string;
  lastNewsReason?: string;
  lastNewsUrgency?: number;
}

interface MarketEvaluation {
  marketId: string;
  question: string;
  yesPrice: number;
  noPrice: number;
  volume: number;
  conviction: number;
  side: 'YES' | 'NO' | 'SKIP';
  reasoning: string;
  timestamp: string;
}

interface BotLog {
  message: string;
  level: 'info' | 'success' | 'error' | 'warning';
  timestamp: string;
}

interface DashboardProps {
  status: any;
  onStatusUpdate?: (update: any) => void;
}

function formatResolveDate(iso?: string): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
    timeZone: 'America/New_York',
    timeZoneName: 'short',
  });
}

function timeAgo(iso?: string): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (d > 0) return `${d}d ago`;
  if (h > 0) return `${h}h ago`;
  return `${m}m ago`;
}

function formatExpiry(resolveTime?: string): { text: string; urgency: string } {
  if (!resolveTime) return { text: '—', urgency: 'none' };
  const ms = new Date(resolveTime).getTime() - Date.now();
  if (ms < 0) return { text: 'Expired', urgency: 'expired' };
  const totalHours = Math.floor(ms / 3600000);
  const d = Math.floor(totalHours / 24);
  const h = totalHours % 24;
  const m = Math.floor((ms % 3600000) / 60000);
  const text = d > 0 ? `${d}d ${h}h` : totalHours > 0 ? `${totalHours}h ${m}m` : `${m}m`;
  const urgency = ms < 2 * 3600000 ? 'urgent' : ms < 24 * 3600000 ? 'soon' : 'ok';
  return { text, urgency };
}


// Retrospective: what would have happened if held to expiry
function TradeRetro({ trade, side }: { trade: Trade; side: string }) {
  const [outcome, setOutcome] = useState<{ resolved: boolean; outcome: string | null; yesPrice: number | null } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`/api/market-outcome?marketId=${trade.marketId}`)
      .then(r => setOutcome(r.data))
      .catch(() => setOutcome(null))
      .finally(() => setLoading(false));
  }, [trade.marketId]);

  if (loading) return <div className="retro-row">⏳ Checking what would have happened…</div>;
  if (!outcome || !outcome.resolved) return null;

  const shares = trade.amount / trade.price;
  const resolvedYesPrice = outcome.outcome === 'Yes' ? 1.0 : 0.0;
  const returnPerShare = side === 'YES' ? resolvedYesPrice : (1 - resolvedYesPrice);
  const counterfactualPnl = shares * returnPerShare - trade.amount;
  const saved = trade.pnl - counterfactualPnl;
  const savedAbs = Math.abs(saved);
  const stopWasBetter = saved > 0;

  return (
    <div className="retro-row">
      <span className="retro-label">If held to expiry ({outcome.outcome} won):</span>
      <span className={`retro-pnl ${counterfactualPnl >= 0 ? 'positive' : 'negative'}`}>
        {counterfactualPnl >= 0 ? '+' : ''}${counterfactualPnl.toFixed(2)}
      </span>
      <span className={`retro-verdict ${stopWasBetter ? 'retro-saved' : 'retro-cost'}`}>
        {stopWasBetter
          ? `→ stop-loss saved $${savedAbs.toFixed(2)}`
          : `→ stop-loss cost $${savedAbs.toFixed(2)}`}
      </span>
    </div>
  );
}

export default function Dashboard({ status, onStatusUpdate }: DashboardProps) {
  const [trades, setTrades] = useState<Trade[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [newsAlerts, setNewsAlerts] = useState<any[]>([]);
  const [metrics, setMetrics] = useState<any>(null);
  const [evaluations, setEvaluations] = useState<MarketEvaluation[]>([]);
  const [logs, setLogs] = useState<BotLog[]>([]);
  const [scanning, setScanning] = useState(false);
  const [pnlHistory, setPnlHistory] = useState<{ timestamp: number; cumulative_pnl: number }[]>([]);
  const [lookaheadHours, setLookaheadHours] = useState(8);
  const [liveTrading, setLiveTrading] = useState(false);
  const [models, setModels] = useState<string[]>([]);
  const [currentModel, setCurrentModel] = useState<string>('');
  const [selectedTrade, setSelectedTrade] = useState<Trade | null>(null);
  const [showClosedTrades, setShowClosedTrades] = useState(false);
  const [closingPosition, setClosingPosition] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'open' | 'history'>('open');
  const [refreshing, setRefreshing] = useState(false);
  const logFeedRef = useRef<HTMLDivElement>(null);

  // Fetch initial data
  useEffect(() => {
    axios.get('/api/trades').then(r => setTrades(r.data)).catch(() => {});
    axios.get('/api/positions').then(r => setPositions(r.data)).catch(() => {});
    axios.get('/api/news-alerts').then(r => setNewsAlerts(r.data)).catch(() => {});
    axios.get('/api/pnl-history').then(r => setPnlHistory(r.data)).catch(() => {});
    axios.get('/api/metrics').then(r => setMetrics(r.data)).catch(() => {});
    axios.get('/api/evaluations').then(r => setEvaluations(r.data)).catch(() => {});
    axios.get('/api/models').then(r => setModels(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    axios.get('/api/config').then(r => {
      setCurrentModel(r.data.aiModel || '');
      setLiveTrading(!!r.data.liveTrading);
      setLookaheadHours(r.data.lookaheadHours || 8);
    }).catch(() => {});
    axios.get('/api/logs').then(r => setLogs(r.data.reverse())).catch(() => {});
  }, []);

  // SSE stream
  useEffect(() => {
    const es = new EventSource('/api/stream');

    es.addEventListener('bot_log', (e) => {
      const log: BotLog = JSON.parse(e.data);
      setLogs(prev => {
        if (prev.some(p => p.timestamp === log.timestamp)) return prev;
        return [...prev, log].slice(-200);
      });
      if (log.message.includes('Starting market scan')) setScanning(true);
      if (log.message.includes('Scan done') || log.message.includes('no markets')) setScanning(false);
    });

    es.addEventListener('market_evaluated', (e) => {
      const ev: MarketEvaluation = JSON.parse(e.data);
      setEvaluations(prev => [ev, ...prev].slice(0, 50));
    });

    es.addEventListener('trade_placed', (e) => {
      const trade: Trade = JSON.parse(e.data);
      setTrades(prev => [trade, ...prev].slice(0, 50));
    });

    es.addEventListener('positions_updated', (e) => {
      const positions: Position[] = JSON.parse(e.data);
      setPositions(positions);
    });

    es.addEventListener('position_closed', (e) => {
      const { market, pnl } = JSON.parse(e.data);
      setTrades(prev => prev.map(t =>
        t.market === market && t.status === 'OPEN'
          ? { ...t, status: 'CLOSED' as const, pnl }
          : t
      ));
      setPositions(prev => prev.filter(p => p.market !== market));
      axios.get('/api/pnl-history').then(r => setPnlHistory(r.data)).catch(() => {});
    });

    es.addEventListener('status_updated', (e) => {
      try {
        const update = JSON.parse(e.data);
        if (onStatusUpdate) onStatusUpdate(update);
      } catch (err) {}
    });

    es.onerror = () => {
      // auto-reconnects
    };

    return () => es.close();
  }, []);

  // Auto-scroll log — scroll within container, not the page
  useEffect(() => {
    if (logFeedRef.current) {
      logFeedRef.current.scrollTop = logFeedRef.current.scrollHeight;
    }
  }, [logs]);

  const triggerScan = async () => {
    setScanning(true);
    try {
      await axios.post(`/api/bot/scan?lookaheadHours=${lookaheadHours}`);
    } catch {}
    setTimeout(() => setScanning(false), 300000);
  };

  const clearTrades = async () => {
    if (liveTrading) {
      alert('Clear is disabled in live trading mode for safety.');
      return;
    }
    if (!window.confirm('Clear all recent trades for a fresh simulation experience?')) return;
    try {
      await axios.post('/api/clear-trades');
      setTrades([]);
    } catch (e) {
      console.error('Clear failed', e);
    }
  };

  const handleCloseAlert = async (market: string) => {
    if (liveTrading) {
      alert('Manual close is disabled in live trading mode for safety.');
      return;
    }
    if (!window.confirm(`Close this bet?\n"${market}"`)) return;
    try {
      await axios.post('/api/close', { market });
      const [alertsRes, posRes] = await Promise.all([
        axios.get('/api/news-alerts'),
        axios.get('/api/positions')
      ]);
      setNewsAlerts(alertsRes.data || []);
      setPositions(posRes.data || []);
    } catch (err: any) {
      alert('Failed to close: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleClosePosition = async (market: string) => {
    if (!window.confirm(`Close this bet?\n"${market.slice(0, 80)}"`)) return;
    setClosingPosition(market);
    try {
      await axios.post('/api/close', { market });
      const posRes = await axios.get('/api/positions');
      setPositions(posRes.data || []);
    } catch (err: any) {
      alert('Failed to close: ' + (err.response?.data?.error || err.message));
    } finally {
      setClosingPosition(null);
    }
  };

  const handleRefreshPositions = async () => {
    setRefreshing(true);
    try {
      await axios.post('/api/bot/update-positions');
      const posRes = await axios.get('/api/positions');
      setPositions(posRes.data || []);
      const tradesRes = await axios.get('/api/trades');
      setTrades(tradesRes.data || []);
    } catch (err: any) {
      alert('Refresh failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setRefreshing(false);
    }
  };

  const changeModel = async (model: string) => {
    setCurrentModel(model);
    try { await axios.post('/api/config', { aiModel: model }); } catch {}
  };

  const logLevelColor: Record<string, string> = {
    info: '#aaa',
    success: '#4ade80',
    error: '#f87171',
    warning: '#fbbf24',
  };

  // Enrich each position with matching trade data (amount spent, resolveTime, reasoning)
  const enrichedPositions = positions.map(p => {
    const trade = trades.find(t =>
      t.status === 'OPEN' &&
      ((t.marketId && t.marketId === p.marketId) || t.market === p.market)
    );
    return {
      ...p,
      resolveTime: p.resolveTime || trade?.resolveTime,
      amount: trade?.amount,
      reasoning: trade?.reasoning,
    };
  });

  const sortedPositions = [...enrichedPositions].sort((a, b) => {
    // Expired positions float to top so they're obvious
    const aExpired = a.resolveTime && new Date(a.resolveTime).getTime() < Date.now();
    const bExpired = b.resolveTime && new Date(b.resolveTime).getTime() < Date.now();
    if (aExpired && !bExpired) return -1;
    if (!aExpired && bExpired) return 1;
    if (!a.resolveTime && !b.resolveTime) return 0;
    if (!a.resolveTime) return 1;
    if (!b.resolveTime) return -1;
    return new Date(a.resolveTime).getTime() - new Date(b.resolveTime).getTime();
  });

  return (
    <div className="dashboard">
      {/* Stats Row */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Capital</div>
          <div className="stat-value">${(status?.capital || 0).toFixed(2)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">P&L</div>
          <div className={`stat-value ${(status?.pnl || 0) >= 0 ? 'positive' : 'negative'}`}>
            {(status?.pnl || 0) >= 0 ? '+' : ''}${(status?.pnl || 0).toFixed(2)}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Positions</div>
          <div className="stat-value">{status?.positions || 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Token Spend</div>
          <div className="stat-value">${(status?.tokenSpend || 0).toFixed(4)}</div>
        </div>
        {metrics && (
          <>
            <div className="stat-card">
              <div className="stat-label">Win Rate</div>
              <div className="stat-value">{(metrics.winRate * 100).toFixed(0)}%</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Sharpe</div>
              <div className="stat-value">{metrics.sharpeRatio.toFixed(2)}</div>
            </div>
          </>
        )}
      </div>

      {/* Controls */}
      <div className="controls-row">
        <div className="bot-status-indicator">
          <span className={`status-dot ${status?.running ? 'running' : 'stopped'}`} />
          {status?.running ? 'Bot running' : 'Bot stopped'}
          {scanning && <span className="scanning-badge">scanning…</span>}
        </div>
        <button
          className="scan-btn"
          onClick={triggerScan}
          disabled={scanning || !!status?.isScanning}
        >
          {scanning ? 'Scanning…' : '▶ Scan Now'}
        </button>
        <button
          className="clear-btn"
          onClick={clearTrades}
          disabled={liveTrading || scanning}
        >
          Clear Trades
        </button>
        <label className="lookahead-control">
          Lookahead:
          <select
            value={lookaheadHours}
            onChange={(e) => {
              const val = parseInt(e.target.value);
              setLookaheadHours(val);
              axios.post('/api/config', { lookaheadHours: val }).catch(() => {});
            }}
          >
            <option value="8">8 hours</option>
            <option value="24">24 hours</option>
            <option value="168">7 days</option>
            <option value="720">30 days</option>
            <option value="0">All markets</option>
          </select>
        </label>
        <span className="last-scan">
          {status?.lastScan ? `Last: ${new Date(status.lastScan).toLocaleTimeString()}` : 'No scan yet'}
        </span>
        {status?.lastScanStats && status.lastScanStats.fetched > 0 && (
          <span className="scan-stats">
            {status.lastScanStats.fetched} fetched → {status.lastScanStats.evaluated} evaluated → {status.lastScanStats.traded} traded
          </span>
        )}
      </div>

      {/* Model selector */}
      <div className="model-row">
        <span className="model-label">🤖 Model</span>
        <select
          className="model-select"
          value={currentModel}
          onChange={(e) => changeModel(e.target.value)}
        >
          {models.length === 0 && <option value={currentModel}>{currentModel || 'Loading…'}</option>}
          {models.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>

      {/* P&L Chart */}
      {pnlHistory.length > 1 && (() => {
        const vals = pnlHistory.map(p => p.cumulative_pnl);
        const minV = Math.min(...vals);
        const maxV = Math.max(...vals);
        const range = maxV - minV || 1;
        const W = 600, H = 80, PAD = 4;
        const pts = pnlHistory.map((p, i) => {
          const x = PAD + (i / (pnlHistory.length - 1)) * (W - PAD * 2);
          const y = H - PAD - ((p.cumulative_pnl - minV) / range) * (H - PAD * 2);
          return `${x.toFixed(1)},${y.toFixed(1)}`;
        }).join(' ');
        const lastVal = vals[vals.length - 1];
        return (
          <div className="panel pnl-chart-panel">
            <div className="panel-header">
              📈 Cumulative P&L
              <span className={`pnl-chart-current ${lastVal >= 0 ? 'positive' : 'negative'}`}>
                {lastVal >= 0 ? '+' : ''}${lastVal.toFixed(2)}
              </span>
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} className="pnl-svg" preserveAspectRatio="none">
              <line x1={PAD} y1={H - PAD - ((-minV) / range) * (H - PAD * 2)} x2={W - PAD} y2={H - PAD - ((-minV) / range) * (H - PAD * 2)} stroke="#333" strokeWidth="0.5" strokeDasharray="2,2" />
              <polyline points={pts} fill="none" stroke={lastVal >= 0 ? '#4ade80' : '#f87171'} strokeWidth="1.5" />
            </svg>
          </div>
        );
      })()}

      {/* Edge Report: is the AI actually out-predicting the market? */}
      <EdgeReport />

      {/* Two-column layout: Evaluations + Live Log */}
      <div className="main-grid">
        {/* AI Evaluations */}
        <div className="panel">
          <div className="panel-header">
            🧠 AI Market Evaluations
            <span className="panel-count">{evaluations.length}</span>
          </div>
          <div className="eval-list">
            {evaluations.length === 0 && (
              <div className="empty-state">No evaluations yet — trigger a scan to start</div>
            )}
            {evaluations.map((ev, i) => (
              <div key={`${ev.marketId}-${i}`} className={`eval-card ${ev.conviction >= 80 ? 'high-conviction' : ''}`}>
                <div className="eval-header">
                  <span className={`eval-side ${ev.side.toLowerCase()}`}>{ev.side}</span>
                  <span className="eval-conviction">{ev.conviction}%</span>
                  <span className="eval-volume">${(ev.volume / 1000).toFixed(0)}k vol</span>
                </div>
                <div className="eval-question">{ev.question}</div>
                <div className="eval-prices">
                  YES {(ev.yesPrice * 100).toFixed(0)}¢ · NO {(ev.noPrice * 100).toFixed(0)}¢
                </div>
                <div className="eval-reasoning">{ev.reasoning}</div>
                <div className="eval-time">{new Date(ev.timestamp).toLocaleTimeString()}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Live Log */}
        <div className="panel">
          <div className="panel-header">
            📡 Live Bot Log
            <span className="panel-count">{logs.length}</span>
          </div>
          <div className="log-feed" ref={logFeedRef}>
            {logs.map((log, i) => (
              <div key={`${log.timestamp}-${i}`} className="log-entry" style={{ color: logLevelColor[log.level] }}>
                <span className="log-time">{new Date(log.timestamp).toLocaleTimeString()}</span>
                <span className="log-msg">{log.message}</span>
              </div>
            ))}

          </div>
        </div>
      </div>

      {/* News Headlines */}
      <div className="panel">
        <div className="panel-header">
          📰 Recent News
          <button onClick={() => axios.get('/api/news-alerts').then(r => setNewsAlerts(r.data || [])).catch(console.error)} style={{marginLeft: 'auto', fontSize: '0.8rem', padding: '2px 8px'}}>↻</button>
          <span className="panel-count">{newsAlerts.length}</span>
        </div>
        <div className="alert-list" style={{maxHeight: '300px', overflowY: 'auto'}}>
          {newsAlerts.length === 0 && <div className="empty-state">No headlines yet — fetched every 30 min when positions are open</div>}
          {newsAlerts.map((alert, i) => (
            <div key={i} className="eval-card" style={{borderLeft: '4px solid #60a5fa'}}>
              <div className="eval-header">
                <span className="alert-market" style={{fontWeight: 'bold'}}>{alert.market?.slice(0, 60)}</span>
                <span style={{fontSize: '0.75rem', color: '#9ca3af'}}>{alert.lastNewsCheck ? new Date(alert.lastNewsCheck).toLocaleTimeString() : ''}</span>
              </div>
              <div style={{fontSize: '0.85rem', color: '#d1d5db', marginTop: '4px', lineHeight: '1.5'}}>
                {(alert.lastNewsReason || '').split(' | ').map((h: string, j: number) => (
                  <div key={j} style={{marginBottom: '2px'}}>• {h}</div>
                ))}
              </div>
              <button
                onClick={() => handleCloseAlert(alert.market)}
                style={{marginTop: '8px', padding: '4px 12px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem'}}
              >
                Close Position
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Portfolio: tabbed Open Bets + History */}
      {(sortedPositions.length > 0 || trades.length > 0) && (
        <div className="panel">
          <div className="panel-header">
            💼 Portfolio
            <div className="portfolio-tabs">
              <button
                className={`portfolio-tab ${activeTab === 'open' ? 'tab-active' : ''}`}
                onClick={() => setActiveTab('open')}
              >
                Open Bets
                {sortedPositions.length > 0 && <span className="tab-count">{sortedPositions.length}</span>}
              </button>
              <button
                className={`portfolio-tab ${activeTab === 'history' ? 'tab-active' : ''}`}
                onClick={() => setActiveTab('history')}
              >
                History
                {trades.filter(t => t.status === 'CLOSED').length > 0 && (
                  <span className="tab-count">{trades.filter(t => t.status === 'CLOSED').length}</span>
                )}
              </button>
              <button
                className="refresh-btn"
                onClick={handleRefreshPositions}
                disabled={refreshing}
                title="Force-check current prices and close expired markets"
              >
                {refreshing ? '…' : '↻'}
              </button>
            </div>
          </div>

          {/* ── Open Bets tab ── */}
          {activeTab === 'open' && (
            <div className="positions-list">
              {sortedPositions.length === 0 && (
                <div className="empty-state">No open bets</div>
              )}
              {sortedPositions.map((p, i) => {
                const resolveTime = p.resolveTime;
                const isExpired = resolveTime ? new Date(resolveTime).getTime() < Date.now() : false;
                const { text: expiryText, urgency } = formatExpiry(resolveTime);
                const profitable = p.pnl >= 0;
                const priceStale = p.currentPrice === 0 && p.avgPrice > 0;

                return (
                  <div key={i} className={`position-card ${profitable ? 'position-card-profit' : 'position-card-loss'} ${isExpired ? 'position-card-expired' : ''}`}>
                    {isExpired && (
                      <div className="expired-banner">⚠ This bet has expired — use the Close button below to settle at current price</div>
                    )}
                    <div className="position-card-top">
                      <div className="position-header-left">
                        <span className={`side-badge side-${(p.side || 'YES').toLowerCase()}`}>{p.side || '?'}</span>
                        <span className="position-market">{p.market}</span>
                      </div>
                      <button
                        className={`close-now-btn ${profitable ? 'close-profitable' : 'close-loss'}`}
                        onClick={() => handleClosePosition(p.market)}
                        disabled={closingPosition === p.market}
                      >
                        {closingPosition === p.market ? 'Closing…' : (
                          <>Close <span className={`pnl-callout ${profitable ? 'positive' : 'negative'}`}>{profitable ? '+' : ''}${p.pnl.toFixed(2)}</span></>
                        )}
                      </button>
                    </div>

                    <div className="position-bet-line">
                      Bet <strong>{p.amount != null ? `$${(p.amount as number).toFixed(2)}` : '—'}</strong>
                      {' on '}<strong>{p.side}</strong>
                      {' at '}<strong>{(p.avgPrice * 100).toFixed(0)}¢</strong>
                      {' · '}{Math.round(p.shares)} shares
                      {p.openedAt && <>{' · placed '}{timeAgo(p.openedAt)}</>}
                    </div>
                    {!isExpired && resolveTime && (() => {
                      const winProfit = p.shares * (1.0 - p.avgPrice);
                      return (
                        <div className="position-win-line">
                          If <strong>{p.side}</strong> wins on {formatResolveDate(resolveTime)} → you make{' '}
                          <span className="positive">+${winProfit.toFixed(2)}</span>
                        </div>
                      );
                    })()}

                    <div className="position-status-line">
                      <span className={`position-pnl-big ${profitable ? 'positive' : 'negative'}`}>
                        {profitable ? '▲' : '▼'} {profitable ? '+' : ''}${p.pnl.toFixed(2)}
                      </span>
                      <span className="position-meta-sep">·</span>
                      {priceStale
                        ? <span className="price-stale">price not updated — hit Refresh</span>
                        : <span className="position-meta-item">now {(p.currentPrice * 100).toFixed(0)}¢</span>
                      }
                      <span className="position-meta-sep">·</span>
                      {isExpired
                        ? <span className="expiry-expired">⏰ expired {new Date(resolveTime!).toLocaleDateString()}</span>
                        : <span className={`expiry-cell expiry-${urgency}`}>⏰ {expiryText}</span>
                      }
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── History tab ── */}
          {activeTab === 'history' && (
            <div className="trades-list">
              {trades.filter(t => t.status === 'CLOSED').length === 0 && (
                <div className="empty-state">No settled trades yet — open bets will appear here once they resolve</div>
              )}
              {trades.filter(t => t.status === 'CLOSED').map((t) => {
                const won = t.pnl > 0;
                const side = t.side === 'BUY' ? 'YES' : 'NO';
                const isEarlyClose = t.closeReason && !t.closeReason.startsWith('resolved') && !t.closeReason.startsWith('price-resolved');
                const marketExpired = t.resolveTime && new Date(t.resolveTime) < new Date();
                const showRetro = isEarlyClose && marketExpired && t.marketId;
                return (
                  <div key={t.id} className={`history-card ${won ? 'history-won' : 'history-lost'}`} onClick={() => setSelectedTrade(t)}>
                    <div className="history-card-top">
                      <span className={`history-outcome ${won ? 'outcome-won' : 'outcome-lost'}`}>
                        {won ? '✓ WON' : '✗ LOST'}
                      </span>
                      <span className="trade-market">{t.market}</span>
                      <span className={`trade-pnl ${won ? 'positive' : 'negative'}`}>
                        {won ? '+' : ''}${t.pnl.toFixed(2)}
                      </span>
                    </div>
                    <div className="history-card-meta">
                      <span className="position-meta-item">
                        Bet ${t.amount.toFixed(2)} on {side} at {(t.price * 100).toFixed(0)}¢
                      </span>
                      <span className="position-meta-sep">·</span>
                      <span className="position-meta-item">opened {new Date(t.timestamp).toLocaleDateString()}</span>
                      {t.closedAt && (
                        <>
                          <span className="position-meta-sep">·</span>
                          <span className="position-meta-item">
                            closed {new Date(t.closedAt).toLocaleDateString()} {new Date(t.closedAt).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                          </span>
                        </>
                      )}
                      {t.closeReason && (
                        <>
                          <span className="position-meta-sep">·</span>
                          <span className="position-meta-item close-reason">{t.closeReason}</span>
                        </>
                      )}
                    </div>
                    {showRetro && <TradeRetro trade={t} side={side} />}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {selectedTrade && (
        <div className="trade-modal-overlay" onClick={() => setSelectedTrade(null)}>
          <div className="trade-modal" onClick={e => e.stopPropagation()}>
            <div className="trade-modal-header">
              <span className={`status-badge status-${selectedTrade.status.toLowerCase()}`}>{selectedTrade.status}</span>
              <span className={`trade-modal-side ${selectedTrade.side === 'BUY' ? 'positive' : 'negative'}`}>{selectedTrade.side}</span>
              <button className="trade-modal-close" onClick={() => setSelectedTrade(null)}>✕</button>
            </div>
            <div className="trade-modal-section">
              <div className="trade-modal-label">Market</div>
              <div className="trade-modal-value">{selectedTrade.market}</div>
            </div>
            <div className="trade-modal-grid">
              <div className="trade-modal-stat">
                <div className="trade-modal-label">Price</div>
                <div className="trade-modal-value">{(selectedTrade.price * 100).toFixed(0)}¢</div>
              </div>
              <div className="trade-modal-stat">
                <div className="trade-modal-label">Amount</div>
                <div className="trade-modal-value">${selectedTrade.amount.toFixed(2)}</div>
              </div>
              <div className="trade-modal-stat">
                <div className="trade-modal-label">P&L</div>
                <div className={`trade-modal-value ${selectedTrade.pnl >= 0 ? 'positive' : 'negative'}`}>{selectedTrade.pnl >= 0 ? '+' : ''}${selectedTrade.pnl.toFixed(2)}</div>
              </div>
              <div className="trade-modal-stat">
                <div className="trade-modal-label">Opened</div>
                <div className="trade-modal-value">{new Date(selectedTrade.timestamp).toLocaleString()}</div>
              </div>
              {selectedTrade.resolveTime && (
                <div className="trade-modal-stat">
                  <div className="trade-modal-label">Resolves</div>
                  <div className="trade-modal-value">{new Date(selectedTrade.resolveTime).toLocaleString()}</div>
                </div>
              )}
              {selectedTrade.marketId && (
                <div className="trade-modal-stat">
                  <div className="trade-modal-label">Market ID</div>
                  <div className="trade-modal-value trade-modal-id">{selectedTrade.marketId}</div>
                </div>
              )}
            </div>
            {selectedTrade.reasoning && (
              <div className="trade-modal-section">
                <div className="trade-modal-label">AI Reasoning</div>
                <div className="trade-modal-reasoning">{selectedTrade.reasoning}</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
